import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
  useColorMode,
  useToast,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import { ThreadStatusBadge } from "./OutreachInbox";
import "./Outreach.css";

// One outreach conversation: the touches we sent, their replies, the outreach
// assistant's answers, and superadmin replies/notes.

const OUTCOME_COLORS = {
  interested: "green", callback_requested: "green", not_interested: "gray", wrong_number: "red",
  voicemail: "blue", no_decision: "yellow", opted_out: "red", no_answer: "gray", error: "red",
};

const ROLE_LABELS = {
  touch: { label: "Campaign touch", color: "blue" },
  prospect: { label: "Contact", color: "green" },
  assistant: { label: "Assistant", color: "purple" },
  agent: { label: "You (team)", color: "teal" },
  note: { label: "Internal note", color: "orange" },
};

function OutreachThreadDetail() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { colorMode } = useColorMode();

  const [thread, setThread] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [asNote, setAsNote] = useState(false);
  const [channel, setChannel] = useState("");
  const [sending, setSending] = useState(false);
  // One id per COMPOSED message: the shared client retries network failures, so
  // the server dedups replays on this and never stores or sends the reply twice.
  const [composeKey, setComposeKey] = useState(() => crypto.randomUUID());

  const mutedColor = colorMode === "light" ? "gray.600" : "gray.400";
  const cardBg = colorMode === "light" ? "gray.50" : "gray.700";

  const fetchThread = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await cqAPI.get(`/outreach/threads/${threadId}?bid=`);
      if (response?.type === "ok") {
        setThread(response.data);
        // Functional update: this callback runs on every refetch, and reading
        // `channel` from the closure would see the value from the render the
        // callback was created in — resetting the picker after every refresh.
        // Seed from the channel the conversation last used (the server's own
        // default for a reply without a channel); fall back to SMS, then email.
        // SMS is offered only when there is a line to reply on — the one that texted us
        // (`sms_line`), else the contact's own phone — so the seed never names a
        // channel the picker cannot show.
        const smsLine = response.data.sms_line || response.data.phone;
        const usable = (c) => (c === "sms" ? !!smsLine : c === "email" ? !!response.data.email : false);
        setChannel((current) => (usable(current) && current)
          || (usable(response.data.last_channel) && response.data.last_channel)
          || (smsLine ? "sms" : "email"));
      } else {
        toast({ title: "Error", description: response?.msg || "Failed to load the thread.", status: "error", duration: 3000, isClosable: true });
      }
    } catch (error) {
      console.error("Error loading outreach thread:", error);
    } finally {
      setIsLoading(false);
    }
  }, [threadId, toast]);

  useEffect(() => { fetchThread(); }, [fetchThread]);

  // A different thread starts with a fresh channel choice (and a fresh compose
  // idempotency key below); without this the previous thread's selection leaks.
  useEffect(() => { setChannel(""); setComposeKey(crypto.randomUUID()); }, [threadId]);

  const send = async () => {
    const body = reply.trim();
    if (!body) return;
    setSending(true);
    try {
      const response = await cqAPI.post(`/outreach/threads/${threadId}/reply`, {
        body,
        internal_note: asNote,
        channel,
        client_message_id: composeKey,
      });
      if (response?.type === "ok") {
        setReply("");
        setComposeKey(crypto.randomUUID());
        if (response.data && response.data.sent === false) {
          toast({ title: "Stored, not delivered", description: response.data.reason || "The message could not be sent.", status: "warning", duration: 6000, isClosable: true });
        }
        await fetchThread();
      } else {
        toast({ title: "Error", description: response?.msg || "Could not send.", status: "error", duration: 4000, isClosable: true });
      }
    } finally {
      setSending(false);
    }
  };

  const setAi = async (paused) => {
    const response = await cqAPI.put(`/outreach/threads/${threadId}/ai`, { paused });
    if (response?.type === "ok") setThread((t) => ({ ...t, ai_paused: response.data.ai_paused }));
  };

  const setStatus = async (status) => {
    const response = await cqAPI.put(`/outreach/threads/${threadId}/status`, { status });
    if (response?.type === "ok") setThread((t) => ({ ...t, status: response.data.status }));
  };

  if (isLoading || !thread) {
    return (
      <Box className="outreach-page page-pad" p={4}>
        <Flex justify="center" align="center" py={10}><Spinner color="blue.500" size="xl" /></Flex>
      </Box>
    );
  }

  return (
    <Box className="outreach-page page-pad" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex align="center" justify="space-between" wrap="wrap" gap={2} mb={2}>
        <Button variant="ghost" pl={0} leftIcon={<FontAwesomeIcon icon={faArrowLeft} />} onClick={() => navigate("/outreach-inbox")}>
          Back to inbox
        </Button>
        <Flex align="center" gap={3} wrap="wrap">
          <ThreadStatusBadge status={thread.status} />
          <Select size="sm" width="160px" value={thread.status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ai">AI handled</option>
            <option value="open">Open (human)</option>
            <option value="closed">Closed</option>
          </Select>
          <FormControl display="flex" alignItems="center" width="auto">
            <FormLabel htmlFor="outreach-ai" mb="0" fontSize="sm">AI answering</FormLabel>
            <Switch id="outreach-ai" isChecked={!thread.ai_paused} onChange={(e) => setAi(!e.target.checked)} />
          </FormControl>
        </Flex>
      </Flex>

      <Text fontSize="xl" fontWeight="bold" textAlign="left">{thread.business_name || "—"}</Text>
      <Text fontSize="sm" color={mutedColor} textAlign="left" mb={4}>
        {[thread.contact_name, thread.email, thread.phone].filter(Boolean).join(" · ")}
        {thread.enrollment ? ` · sequence ${thread.enrollment.status}` : ""}
        {thread.enrollment?.source === "manual" ? " · hand-picked" : ""}
        {thread.enrollment?.customer === true ? " · customer" : thread.enrollment?.customer === false ? " · prospect" : ""}
        {thread.enrollment?.tz ? ` · ${thread.enrollment.tz}` : ""}
      </Text>

      <Flex direction="column" gap={3} mb={6}>
        {(thread.messages || []).map((message) => {
          const meta = ROLE_LABELS[message.role] || { label: message.role, color: "gray" };
          return (
            <Box key={message._id} bg={cardBg} borderRadius="lg" p={3} borderLeftWidth="3px"
              borderLeftColor={`${meta.color}.400`} maxW="860px">
              <Flex align="center" gap={2} mb={1} wrap="wrap">
                <Badge colorScheme={meta.color} variant="subtle">{meta.label}</Badge>
                <Badge variant="outline">{message.channel}</Badge>
                {message.step !== undefined && message.step !== null && message.role === "touch" && (
                  <Badge variant="outline">step {message.step + 1}</Badge>
                )}
                {message.delivery?.status && (
                  <Badge colorScheme={["failed", "undelivered", "bounced", "complaint"].includes(message.delivery.status) ? "red" : "gray"} variant="subtle">
                    {message.delivery.status}
                  </Badge>
                )}
                <Text fontSize="xs" color={mutedColor}>
                  {message.author_name ? `${message.author_name} · ` : ""}{moment(message.created).format("MMM D, h:mm a")}
                </Text>
              </Flex>
              {message.subject ? (
                <Text fontWeight="semibold" fontSize="sm" textAlign="left">{message.subject}</Text>
              ) : null}
              {message.channel === "call" && message.role === "touch" ? (
                <>
                  <Text fontSize="xs" color={mutedColor} textAlign="left">AI call · goal</Text>
                  <Text className="outreach-message-body" fontSize="sm">{message.body}</Text>
                  {message.call && (
                    <Box mt={2} borderTopWidth="1px" pt={2}>
                      <Flex gap={2} align="center" wrap="wrap" mb={1}>
                        <Badge colorScheme={OUTCOME_COLORS[message.call.outcome] || "gray"}>{String(message.call.outcome || "").replace(/_/g, " ")}</Badge>
                        {typeof message.call.duration_seconds === "number" && <Text fontSize="xs" color={mutedColor}>{message.call.duration_seconds}s</Text>}
                        {message.call.answered_by && <Text fontSize="xs" color={mutedColor}>answered by {message.call.answered_by}</Text>}
                        {message.call.ended_by && <Text fontSize="xs" color={mutedColor}>ended by {message.call.ended_by}</Text>}
                        {message.call.callback_time && <Badge variant="outline">call back: {message.call.callback_time}</Badge>}
                      </Flex>
                      {message.call.summary && <Text fontSize="sm" textAlign="left" mb={1}>{message.call.summary}</Text>}
                      {message.call.error && <Text fontSize="xs" color="red.400" textAlign="left">{message.call.error}</Text>}
                      {(message.call.transcript || []).length > 0 && (
                        <Box mt={1}>
                          {message.call.transcript.map((turn, idx) => (
                            <Text key={idx} fontSize="xs" textAlign="left" color={turn.role === "agent" ? mutedColor : undefined}>
                              <Text as="span" fontWeight="semibold">{turn.role === "agent" ? "Agent" : "Contact"}:</Text> {turn.text}
                            </Text>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Text className="outreach-message-body" fontSize="sm">{message.body}</Text>
              )}
              {message.delivery?.detail && (
                <Text fontSize="xs" textAlign="left" mt={1}
                  color={["failed", "undelivered", "bounced", "complaint"].includes(message.delivery.status) ? "red.400" : mutedColor}>
                  {message.delivery.detail}
                </Text>
              )}
            </Box>
          );
        })}
      </Flex>

      <Box borderWidth="1px" borderRadius="lg" p={3} maxW="860px">
        <Flex gap={3} mb={2} align="center" wrap="wrap">
          <Button size="xs" variant={asNote ? "outline" : "solid"} onClick={() => setAsNote(false)}>Reply</Button>
          <Button size="xs" variant={asNote ? "solid" : "outline"} colorScheme="orange" onClick={() => setAsNote(true)}>Internal note</Button>
          {!asNote && (
            <Select size="xs" width="150px" value={channel} onChange={(e) => setChannel(e.target.value)}>
              {/* Only reachable before the seed lands or when the thread has no usable
                  channel; an empty value lets the server pick the last-used channel. */}
              {!channel && <option value="">auto (last used)</option>}
              {(thread.sms_line || thread.phone) && <option value="sms">SMS</option>}
              {thread.email && <option value="email">Email</option>}
            </Select>
          )}
        </Flex>
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder={asNote ? "Write a note for the team…" : "Write a reply to the contact…"}
          rows={3}
        />
        <Flex justify="flex-end" mt={2}>
          <Button size="sm" onClick={send} isLoading={sending} isDisabled={!reply.trim()}>
            {asNote ? "Add note" : "Send"}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}

export default OutreachThreadDetail;
